import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  MapPin, 
  X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';

interface Address {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  notes?: string;
  usage_count: number;
  is_favorite: boolean;
}

interface AddressQuickSearchProps {
  onSelectAddress: (address: Address) => void;
  placeholder?: string;
  className?: string;
}

const AddressQuickSearch: React.FC<AddressQuickSearchProps> = ({
  onSelectAddress,
  placeholder = "Buscar dirección en agenda...",
  className = ""
}) => {
  const { toast } = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [filteredAddresses, setFilteredAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Simple icon for all addresses
  const addressIcon = <MapPin className="h-4 w-4" />;

  // Load addresses from database
  useEffect(() => {
    loadAddresses();
  }, []);

  // Filter addresses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredAddresses([]);
      setIsOpen(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = addresses.filter(addr => 
      addr.name.toLowerCase().includes(query) ||
      addr.address.toLowerCase().includes(query)
    );

    // Sort by usage count, then by relevance
    filtered.sort((a, b) => {
      if (a.usage_count !== b.usage_count) return b.usage_count - a.usage_count;
      
      // Prioritize exact matches in name
      const aNameMatch = a.name.toLowerCase().includes(query);
      const bNameMatch = b.name.toLowerCase().includes(query);
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      
      return a.name.localeCompare(b.name);
    });

    setFilteredAddresses(filtered);
    setIsOpen(filtered.length > 0);
    setSelectedIndex(-1);
  }, [searchQuery, addresses]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredAddresses.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredAddresses.length) {
            // Si hay un índice seleccionado, usar ese
            handleSelectAddress(filteredAddresses[selectedIndex]);
          } else if (filteredAddresses.length > 0) {
            // Si no hay índice seleccionado pero hay resultados, seleccionar el primero
            handleSelectAddress(filteredAddresses[0]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          inputRef.current?.blur();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredAddresses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAddresses = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('logistics_addresses')
        .select('*')
        .order('usage_count', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      setAddresses(data || []);
    } catch (error: any) {
      console.error('Error loading addresses:', error);
      toast.error('Error', error.message || 'Error al cargar las direcciones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAddress = (address: Address) => {
    onSelectAddress(address);
    setSearchQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };



  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (searchQuery.trim() && filteredAddresses.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
              setIsOpen(false);
              setSelectedIndex(-1);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredAddresses.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
              No se encontraron direcciones
            </div>
          ) : (
            <div className="py-1">
              {filteredAddresses.map((address, index) => (
                <div
                  key={address.id}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    index === selectedIndex
                      ? 'bg-indigo-50 dark:bg-indigo-900/30'
                      : index === 0 && selectedIndex === -1
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => handleSelectAddress(address)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5 text-blue-600">
                      {addressIcon}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {address.name}
                        </h4>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {address.address}
                      </p>
                      
                      <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500 dark:text-gray-500">
                        <span>📍 {address.usage_count} usos</span>
                        {address.notes && (
                          <span className="truncate">📝 {address.notes}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressQuickSearch;
